var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as Long from "long";
import { selectBalancingBuilding, selectBalancingBuildings, } from "../balancingSelectors/selectBalancingBuildings";
import { selectBalancingRecipe } from "../balancingSelectors/selectBalancingRecipes";
import { selectBuildingsCategoryMap } from "../balancingSelectors/selectBalancingUiDefinitions";
import { IdModal } from "../features/modals/IdModal";
import { BuildingCategory } from "../features/player-base/buildings/BuildingCategory";
import { TypeBuildingAction } from "../features/player-base/buildings/reducerBuildings";
import { BuildingStateType, } from "../utils/network/generated/protoTypes";
import { utilBuildings } from "../utils/utilBuildings";
import { selectConstructionTimeBuffAmount } from "./selectBuffsInfo";
import { selectRunningEventInfoByTypeId } from "./selectEventInfo";
import { selectModalTabId } from "./selectModalTabId";
import { selectWorkersInfo } from "./selectWorkersInfo";
var findProductionByBuildingId = function (state, id) {
    var farmId = state.playerBase.base.baseId;
    var production = state.productionInfo.productions.find(function (prod) { return prod.buildingId === id && prod.farmId === farmId; });
    if (production != undefined) {
        return {
            production: production,
            recipe: selectBalancingRecipe(production.recipeId),
        };
    }
    return undefined;
};
export var selectAllExistingBuildingInfo = function (state, includeMoving) {
    if (includeMoving === void 0) { includeMoving = false; }
    var existingBuildings = state.baseInfo.buildings.map(function (proto) { return ({
        balancing: selectBalancingBuilding(proto.typeId),
        production: findProductionByBuildingId(state, proto.id),
        proto: proto,
    }); });
    if (!includeMoving) {
        return existingBuildings;
    }
    var buildingsState = state.playerBase.buildings;
    var movingBuilding = buildingsState.building && buildingsState.action === TypeBuildingAction.Move;
    return movingBuilding ? existingBuildings.concat([buildingsState.building]) : existingBuildings;
};
export var selectExistingBuildingInfo = function (state, proto) { return ({
    balancing: selectBalancingBuilding(proto.typeId),
    production: findProductionByBuildingId(state, proto.id),
    proto: proto,
}); };
export var selectBuildingInfoById = function (state, buildingId) {
    var proto = state.baseInfo.buildings.find(function (building) { return building.id === buildingId; });
    return proto != undefined ? selectExistingBuildingInfo(state, proto) : undefined;
};
export var selectBuildingInfoByTypeId = function (state, typeId) {
    var proto = state.baseInfo.buildings.find(function (building) { return building.typeId === typeId; });
    if (!proto) {
        return undefined;
    }
    return selectExistingBuildingInfo(state, proto);
};
export var selectBuildingsInfoByTypeId = function (state, typeId) {
    var buildingProtos = state.baseInfo.buildings.filter(function (building) { return building.typeId === typeId; });
    if (buildingProtos.length) {
        return buildingProtos.map(function (building) { return selectExistingBuildingInfo(state, building); });
    }
    else {
        return [];
    }
};
export var selectSelectedBuildingInfo = function (state) {
    return state.playerBase.buildings.building;
};
export var selectBuildingLevelInfo = function (state, buildingTypeId, level) {
    var buildingInfo = selectBalancingBuilding(buildingTypeId);
    level = Math.max(level - 1, 0);
    return buildingInfo ? buildingInfo.stage[level] : undefined;
};
export var selectBuildingCount = function (state, id) {
    var buildingCountMap = state.baseInfo.buildings.reduce(function (acc, building) {
        var typeId = building.typeId;
        acc.set(typeId, (acc.get(typeId) || 0) + 1);
        return acc;
    }, new Map());
    (state.inventoryInfo.building || []).forEach(function (building) {
        var buildingCount = building.amount.toNumber() + (buildingCountMap.get(building.typeId) || 0);
        buildingCountMap.set(building.typeId, buildingCount);
    });
    return buildingCountMap.get(id) || 0;
};
export var selectExistingBuildingHigherStage = function (state, id) {
    var existingBuildings = selectAllExistingBuildingInfo(state);
    var buildingStage = existingBuildings
        .filter(function (val) { return val.proto.typeId === id; })
        .map(function (val) {
        return val.proto.stage;
    });
    var higherStage = Math.max.apply(Math, buildingStage);
    return higherStage || 1;
};
export var selectShopCategoryBuildingAmount = function (state, shopCategory) {
    var existingBuildings = selectAllExistingBuildingInfo(state);
    var buildingStageMap = existingBuildings.reduce(function (acc, value) {
        if (value.balancing.shopCategory === shopCategory) {
            acc.set(value.balancing.shopCategory, (acc.get(value.balancing.shopCategory) || 0) + 1);
        }
        return acc;
    }, new Map());
    return buildingStageMap.get(shopCategory) || 0;
};
export var selectBuildingConstructionTime = function (state, buildingTypeId, level) {
    var buildingLevelInfo = selectBuildingLevelInfo(state, buildingTypeId, level);
    var constructionTimeBuff = 0;
    if (buildingLevelInfo) {
        constructionTimeBuff = selectConstructionTimeBuffAmount(state, buildingLevelInfo.constructionTime);
    }
    return buildingLevelInfo
        ? constructionTimeBuff > 0
            ? constructionTimeBuff
            : buildingLevelInfo.constructionTime
        : 0;
};
export var selectBuildingXpInfo = function (state, buildingTypeId, level) {
    var buildingLevelInfo = selectBuildingLevelInfo(state, buildingTypeId, level);
    return buildingLevelInfo ? buildingLevelInfo.xp : undefined;
};
export var selectBuildingHappiness = function (state, buildingTypeId, level) {
    var buildingLevelInfo = selectBuildingLevelInfo(state, buildingTypeId, level);
    return buildingLevelInfo ? buildingLevelInfo.happiness || 0 : 0;
};
export var selectBuildingWorkers = function (state, buildingTypeId, level) {
    var buildingLevelInfo = selectBuildingLevelInfo(state, buildingTypeId, level);
    return buildingLevelInfo ? buildingLevelInfo.workers : 0;
};
export var selectBuildingRequirementLevel = function (state, building) {
    var targetBuildingCount = selectBuildingCount(state, building.id) + 1;
    var requiredPlayerLevel = 0;
    var requiredAdventurerLevel = 0;
    if (building.reqLevelPerInstance != undefined) {
        var reqLevel = building.reqLevelPerInstance.find(function (req) { return req.buildingCount === targetBuildingCount; });
        if (reqLevel != undefined) {
            requiredPlayerLevel = reqLevel.requiredPlayerLevel || 0;
            requiredAdventurerLevel = reqLevel.requAdventurerLevel || 0;
        }
    }
    var buildStage = building.stage[0];
    if (buildStage != undefined) {
        requiredPlayerLevel = Math.max(requiredPlayerLevel, buildStage.requPlayerLevel || 0);
        requiredAdventurerLevel = Math.max(requiredAdventurerLevel, buildStage.requAdventurerLevel || 0);
    }
    return {
        requiredAdventurerLevel: requiredAdventurerLevel,
        requiredPlayerLevel: requiredPlayerLevel,
    };
};
export var selectBuildingsByModalTabId = function (state) {
    var availableBuildings = [];
    var modalStack = state.modals.modalStack;
    if (modalStack.length === 0) {
        return availableBuildings;
    }
    var type = selectModalTabId(state, IdModal.BuildingList);
    if (type && type === BuildingCategory.Stored) {
        availableBuildings = selectBuildingFromInventory(state);
    }
    else if (type && state.baseInfo.buildings) {
        var buildingUsableIds_1 = selectBuildingsCategoryMap()[type];
        selectBalancingBuildings().forEach(function (value, key) {
            var existingCount = selectBuildingCount(state, key);
            var requirementLevel = selectBuildingRequirementLevel(state, value);
            var building = __assign({}, value, { existingCount: existingCount }, requirementLevel);
            availableBuildings.push(building);
        });
        availableBuildings = availableBuildings
            .filter(function (value) {
            return (value.farmId == undefined || value.farmId === state.baseInfo.farmId) &&
                (value.requEventId == undefined ||
                    selectRunningEventInfoByTypeId(state, value.requEventId) != undefined) &&
                buildingUsableIds_1.indexOf(value.id) !== -1;
        })
            .sort(function (b1, b2) {
            return (b2.requEventId || 0) - (b1.requEventId || 0);
        });
    }
    return availableBuildings;
};
export var selectSelectedBuildingProduct = function (state) {
    var building = selectSelectedBuildingInfo(state);
    if (building) {
        return building.production.recipe.product;
    }
    return undefined;
};
export var selectSelectedBuildingProductionXp = function (state) {
    var building = selectSelectedBuildingInfo(state);
    if (building) {
        return building.production.recipe.xp.amount;
    }
    return undefined;
};
export var selectIsBuildingStoreable = function (state) {
    var buildingInfo = selectSelectedBuildingInfo(state);
    if (buildingInfo) {
        if (buildingInfo.balancing.isStoreable &&
            buildingInfo.proto.state !== BuildingStateType.CONSTRUCTING) {
            var doesBuildingAlreadyExist = state.playerBase.buildings.action !== TypeBuildingAction.Construct &&
                state.playerBase.buildings.action !== TypeBuildingAction.Unstore;
            return doesBuildingAlreadyExist && !selectSelectedBuildingIsProducing(state);
        }
    }
    return false;
};
export var selectIsBuildingDestructible = function (state) {
    var buildingInfo = selectSelectedBuildingInfo(state);
    var doesBuildingAlreadyExists = state.playerBase.buildings.action !== TypeBuildingAction.Construct &&
        state.playerBase.buildings.action !== TypeBuildingAction.Unstore;
    return buildingInfo && doesBuildingAlreadyExists
        ? buildingInfo.balancing.isDestructible
        : false;
};
export var selectSelectedBuildingIsProducing = function (state) {
    var buildingInfo = selectSelectedBuildingInfo(state);
    return buildingInfo !== undefined ? utilBuildings.isProducing(buildingInfo) : false;
};
export var selectSelectedBuildingIsUpdating = function (state) {
    var buildingInfo = selectSelectedBuildingInfo(state);
    return (buildingInfo !== undefined &&
        buildingInfo.proto.remainingTime !== undefined &&
        buildingInfo.proto.remainingTime > 0);
};
export var selectIsBuildingPlaceable = function (state) {
    return state.playerBase.buildings.isPlacementValid;
};
export var selectIsBuildingPlacedTemporarily = function (state) {
    return state.playerBase.buildings.isPlacedTemporarily;
};
export var selectSelectedBuildingIsPremium = function (state) {
    return state.playerBase.buildings.premium;
};
export var selectBuildingAction = function (state) {
    return state.playerBase.buildings.action;
};
export var selectBuildingActionType = function (state) {
    return state.playerBase.buildings.type;
};
export var selectIsGridVisible = function (state) {
    var action = state.playerBase.buildings.action;
    return (action === TypeBuildingAction.Construct ||
        action === TypeBuildingAction.Move ||
        action === TypeBuildingAction.Unstore);
};
export var selectBuildingFromInventory = function (state) {
    var availableBuildings = [];
    var buildingsType = state.inventoryInfo.building;
    buildingsType.forEach(function (building) {
        var buildingData = selectBalancingBuilding(building.typeId);
        var amount = building.amount;
        var stageId = building.stageId;
        var buildingInfo = __assign({}, buildingData, { amount: amount, stageId: stageId });
        availableBuildings.push(buildingInfo);
    });
    return availableBuildings;
};
export var selectBuildingStageInfo = function (building, stageIndex) {
    return building.balancing.stage[stageIndex];
};
export var selectBuildingStageXpAmount = function (building, stageIndex) {
    return selectBuildingStageInfo(building, stageIndex).xp
        ? Long.fromValue(selectBuildingStageInfo(building, stageIndex).xp.amount || 0)
        : Long.ZERO;
};
export var selectBuildingUpgradeInfo = function (
// state: IState,
building) {
    var balancingStageIndex = utilBuildings.getPreviousProtoStage(building);
    if (building.proto.state === BuildingStateType.UPGRADING) {
        balancingStageIndex += 1;
    }
    var currentStage = selectBuildingStageInfo(building, balancingStageIndex);
    var nextStage = selectBuildingStageInfo(building, balancingStageIndex + 1);
    return {
        currentStage: currentStage,
        nextStage: nextStage,
    };
};
export var selectBuildingUpgradeLevelRequirements = function (state, building, premium) {
    if (premium === void 0) { premium = false; }
    var buildingUpgradeInfo = selectBuildingUpgradeInfo(building);
    var stage = buildingUpgradeInfo.nextStage;
    if (!stage) {
        return undefined;
    }
    if (premium && stage.premium) {
        var requiredPlayerLevel = stage.premium.requPlayerLevel;
        var requiredAdventurerLevel = stage.premium.requAdventurerLevel;
        return {
            requiredAdventurerLevel: requiredAdventurerLevel,
            requiredPlayerLevel: requiredPlayerLevel,
        };
    }
    if (!premium && stage.standard) {
        var requiredPlayerLevel = stage.standard.requPlayerLevel;
        var requiredAdventurerLevel = stage.standard.requAdventurerLevel;
        return {
            requiredAdventurerLevel: requiredAdventurerLevel,
            requiredPlayerLevel: requiredPlayerLevel,
        };
    }
    return undefined;
};
export var selectBuildingPurchaseLevelRequirement = function (state, building) {
    var playerInfo = state.playerInfo;
    var buildingLevelRequirement = selectBuildingRequirementLevel(state, building.balancing);
    var playerAdventureLevel = playerInfo.adventurerLevel || 0;
    return {
        missingPlayerAdventureLevel: buildingLevelRequirement.requiredAdventurerLevel
            ? buildingLevelRequirement.requiredAdventurerLevel - playerAdventureLevel
            : 0,
        missingPlayerLevel: buildingLevelRequirement.requiredPlayerLevel
            ? buildingLevelRequirement.requiredPlayerLevel - playerInfo.level
            : 0,
    };
};
export var selectBuildingPurchaseWorkerRequirement = function (state, building, stageIndex) {
    if (stageIndex === void 0) { stageIndex = 0; }
    var freeWorkers = selectWorkersInfo(state).free;
    var workerRequirement = selectBuildingStageInfo(building, stageIndex).workers;
    return {
        freeWorkers: freeWorkers,
        workerRequirement: workerRequirement,
    };
};
export var selectBuildingRemovedProduction = function (state, buildingId) {
    return state.productionInfo.removedProductions.find(function (production) { return production.buildingId === buildingId; });
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectExistingBuildingInfo.ts
// module id = 18
// module chunks = 3